import { useQuery } from '@apollo/client'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import { Column } from 'material-table'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MediaField from './MediaField'
import { MediaFieldView } from './MediaFieldView'
import NodeMediaLink from './NodeMediaLink'
import { mediaDocumentTypesQuery, mediaDocumentTypesQueryVariables, NodeMediaInput } from '../../generated/graphql'
import DataHelper, { EntityNames } from '../../helpers/DataHelper'
import { requiredField } from '../../helpers/validations'
import { useBranchFilteredQuery } from '../../hooks/BranchFilterHook'
import { CanBeNodeInterface } from '../../providers/UserContext'
import Medias from '../../schema/Medias'
import AutocompleteField from '../AutocompleteField'
import GraphqlError from '../GraphqlError'
import Loading from '../Loading'
import AlertDialog from '../dialogs/AlertDialog'
import { AclTable } from '../table/AclTable'

type NodeMediaRow = NodeMediaInput & CanBeNodeInterface

interface Props {
  ownerNode?: CanBeNodeInterface
  files: NodeMediaInput[]
  entityName?: EntityNames
  fileSources?: Record<string, string>
  helpText?: string
  showQuickAdd?: boolean
  showSource?: boolean
  title?: string
  setFiles(tags: NodeMediaInput[]): void
}

const sourceLabels: Record<string, string> = {
  eventType: 'Appointment type default',
  event: 'Event override',
}

const FilesTable: React.FC<Props> = (props) => {
  const {
    files,
    ownerNode,
    setFiles,
    entityName = EntityNames.CalendarEvent,
    fileSources,
    helpText,
    showQuickAdd = false,
    showSource = false,
    title,
  } = props
  const { t } = useTranslation()
  const branchFilter = useBranchFilteredQuery()
  const { loading, error, data } = useQuery<mediaDocumentTypesQuery, mediaDocumentTypesQueryVariables>(
    Medias.GET_DOCUMENT_TYPES,
    branchFilter,
  )
  const [alert, setAlert] = useState<string | null>(null)
  const baseTypes = data?.mediaDocumentTypes
  const types = useMemo(() => {
    return (
      baseTypes?.map((dt) => ({
        inputValue: dt.id,
        text: dt.name,
      })) || []
    )
  }, [baseTypes])

  const usedDocumentTypeIds = useMemo(() => {
    return new Set(files.map((f) => f.documentTypeId).filter(Boolean))
  }, [files])

  const quickAddTypes = useMemo(() => {
    if (!showQuickAdd || !baseTypes) {
      return []
    }
    return baseTypes.filter((dt) => !usedDocumentTypeIds.has(dt.id))
  }, [baseTypes, showQuickAdd, usedDocumentTypeIds])

  const finalFiles: NodeMediaRow[] = useMemo(() => {
    return files.map((f) => ({
      ...f,
      __typename: EntityNames.NodeMedia,
      canBeDeleted: ownerNode ? ownerNode.canBeUpdated : true,
      canBeUpdated: ownerNode ? ownerNode.canBeUpdated : true,
    }))
  }, [files, ownerNode])

  const addDocumentType = (documentTypeId: string) => {
    if (usedDocumentTypeIds.has(documentTypeId)) {
      return
    }
    const docType = baseTypes?.find((dt) => dt.id === documentTypeId)
    setFiles([...files, { documentTypeId, id_media: docType?.defaultMedia?.id || '' }])
  }

  const columns: Column<NodeMediaRow>[] = useMemo(() => {
    const cols: Column<NodeMediaRow>[] = [
      {
        title: t('Document type'),
        field: 'documentTypeId',
        validate: (rowData) => {
          if (rowData.id_media && !rowData.documentTypeId) {
            return t('Document type is required when a file is attached')
          }
          return true
        },
        render: (nodeData) => {
          return nodeData.documentTypeId && baseTypes?.find((mdt) => mdt.id === nodeData.documentTypeId)?.name
        },
        editComponent: (editProps) => {
          return (
            <AutocompleteField
              value={types.find((type) => editProps.value === type.inputValue) || null}
              options={types}
              fieldLabel={t('Type')}
              onChange={(val: string) => {
                editProps.onChange(val)
              }}
            />
          )
        },
      },
      {
        title: t('File'),
        field: 'id_media',
        validate: requiredField('id_media'),
        editComponent: (editProps) => {
          return (
            <MediaField
              ownerNodeId={ownerNode?.id}
              idMedia={editProps.value}
              notDeleteMedia={!!editProps.rowData.id}
              onChange={(id) => {
                editProps.onChange(id)
              }}
            />
          )
        },
        render: (nodeData) => {
          if (!nodeData.id_media) {
            return t('No file attached')
          }
          return (
            <NodeMediaLink id={nodeData && nodeData.id}>
              <MediaFieldView idMedia={nodeData.id_media} />
            </NodeMediaLink>
          )
        },
      },
    ]

    if (showSource) {
      cols.push({
        title: t('Source'),
        field: 'documentTypeId',
        editable: 'never' as const,
        render: (nodeData) => {
          const source = nodeData.documentTypeId ? fileSources?.[nodeData.documentTypeId] : undefined
          if (!source) {
            return null
          }
          return <Chip size="small" label={t(sourceLabels[source] || source)} />
        },
      })
    }

    return cols
  }, [baseTypes, fileSources, ownerNode?.id, showSource, t, types])

  if (error) {
    return <GraphqlError error={error} />
  }

  if (loading) {
    return <Loading />
  }

  if (!baseTypes?.length) {
    return (
      <Alert severity="warning">
        {t('No documents defined yet. Go to Settings → Documents to add your first document.')}
      </Alert>
    )
  }

  return (
    <div>
      {helpText && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {helpText}
        </Alert>
      )}
      {quickAddTypes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('Click to add a document, then attach the file in the table:')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {quickAddTypes.map((dt) => (
              <Chip key={dt.id} label={dt.name} clickable variant="outlined" onClick={() => addDocumentType(dt.id)} />
            ))}
          </Box>
        </Box>
      )}
      <AlertDialog open={!!alert} title={t('Validation error')} text={alert} onClose={() => setAlert(null)} />
      <AclTable<NodeMediaRow>
        title={title || t('Documents')}
        columns={columns}
        data={finalFiles}
        showInfoColumn={false}
        entityName={entityName}
        editable={{
          onRowAdd: async (newData) => {
            if (newData.id_media && !newData.documentTypeId) {
              setAlert(t('Document type is required when a file is attached'))
              return
            }
            setFiles([...files, DataHelper.cleanInputData(newData)])
          },
          onRowUpdate: async (newData) => {
            if (newData.id_media && !newData.documentTypeId) {
              setAlert(t('Document type is required when a file is attached'))
              return
            }
            setFiles(
              files.map((f) => {
                if (f.id === newData.id) {
                  return DataHelper.cleanInputData(newData)
                }
                return f
              }),
            )
          },
          onRowDelete: async (oldData) => {
            setFiles(
              files.filter((f) => {
                if (oldData.id) {
                  return f.id !== oldData.id
                }
                return (
                  f.id ||
                  (!f.id && f.id_media !== oldData.id_media) ||
                  (f.id && f.id_media === oldData.id_media && f.documentTypeId !== oldData.documentTypeId)
                )
              }),
            )
          },
        }}
      />
    </div>
  )
}

export default FilesTable
